/**
 * generateUuidV4 (pwa-offline-first Slice 7) — RFC 4122 v4 UUID generator
 * with an iOS-compat fallback for the check-in/outbox id generation
 * (`clientRequestId`, Slice 1's idempotency key AND the Dexie outbox
 * primary key).
 *
 * `crypto.randomUUID()` only landed in Safari/iOS 15.4 (March 2022) — every
 * `CheckinPage` call site used it directly with NO fallback, so on an older
 * iOS device it would throw a `TypeError` and check-in (online AND offline)
 * would be entirely broken, not degrade gracefully. The backend validates
 * `clientRequestId` as `z.string().uuid()` (Slice 1), so a non-RFC4122
 * fallback (e.g. `Date.now()+Math.random()`, as used by the out-of-scope
 * Flash-mode local id) would 400 on the backend instead — equally broken,
 * just later and less obviously.
 *
 * `crypto.getRandomValues()` — the WebCrypto primitive `randomUUID()` is
 * itself built on — is part of the Web Crypto API baseline and has been
 * supported since iOS 11, so building the UUID by hand from it removes
 * `randomUUID()` as the binding constraint on this feature's device floor.
 */
export function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error(
      'generateUuidV4: neither crypto.randomUUID() nor crypto.getRandomValues() is available in this environment',
    );
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // RFC 4122 §4.4 — set the 4 version bits and the 2 variant bits.
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
